import React, { useState } from 'react';
import styled from 'styled-components';
import { useSwipeable } from 'react-swipeable';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';
import { fourTheoryLocales } from '../../locales/firstScreen';

import { GlobalFonts } from '../../styles/GlobalFonts';
import { Footer } from '../UI/Footer/Footer';

const LANG_KEY = 'stonpools-lang';

const Container = styled.div`
  background: #151515;
  min-height: 100vh;
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  width: 100%;
  height: min(110px, 15vh);
  margin: 50px 0 30px;
  padding: min(13px, 2vh);
  border-radius: 20px;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url(/assets/background.gif) center/cover no-repeat;
    mix-blend-mode: normal;
    opacity: 0.9;
    border-radius: 20px;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #0180ff;
    opacity: 1;
    mix-blend-mode: multiply;
    border-radius: 20px;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const Title = styled.h2`
  color: #ffffff;
  font-family: 'Manrope', sans-serif;
  font-size: clamp(40px, 5vw, 34px);
  font-weight: 600;
  margin-bottom: min(8px, 1.2vh);
  margin-top: min(1px, 0.2vh);
  line-height: 100%;
`;

const TheoryButton = styled.button`
  background: none;
  border: 2px solid #ffffff;
  border-radius: 12px;
  padding: 5px 7px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  line-height: 100%;
`;

const QuestionBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  position: relative;
`;

const QuestionText = styled.span`
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: clamp(26px, 5vw, 26px);
  font-weight: 600;
  line-height: 90%;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  text-align: center;
  white-space: pre-line;
`;

const Description = styled.p`
  color: #b4b4b4;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  text-align: center;
  margin: 20px 0;
  line-height: 145%;
  letter-spacing: -0.01em;
  white-space: pre-line;

  strong {
    color: #ffffff;
    font-weight: 700;
  }
`;

const ScrollContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 10px;
  padding-bottom: 100px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  margin: 0 10px;
`;

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  align-self: stretch;
  text-align: left;
  padding: 18px 21px;
  margin-bottom: 10px;
  border-radius: 12px;
  border: 2px solid rgba(180, 180, 180, 0.2);
  background: none;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ButtonText = styled.span`
  flex: 1;
  color: #ffffff;
  font-size: 13px;
  font-weight: bold;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
`;

const ButtonIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: fill;
`;

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: #0180ff;
  width: 100%;
  max-width: 380px;
  border-radius: 20px;
  padding: 30px 20px;
  margin: 10px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalTitle = styled.h3`
  font-family: 'Manrope', sans-serif;
  font-size: 26px;
  font-weight: 600;
  line-height: 90%;
  color: #ffffff;
  margin: 0 0 16px 0;
  text-align: center;
  max-width: 90%;
`;

const ModalText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #ffffff;
  text-align: center;
  line-height: 145%;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  white-space: pre-line;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 22px;
  right: 22px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  width: 12px;
  height: 12px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 15px;
    height: 2px;
    background: #ffffff;
    top: 50%;
    left: 50%;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
`;

const HomeButtonIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const HomeButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  margin: 0;
  background-color: transparent;
`;

// Добавьте состояние для модального окна
const modalVariants = {
  hidden: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.2,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      delay: 0.1,
    },
  },
};

const modalContentVariants = {
  hidden: {
    scale: 0.8,
    opacity: 0,
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    scale: 0.8,
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn',
    },
  },
};

const FourTheory5: React.FC<{
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  toHome?: () => void;
}> = ({ onSwipeLeft, onSwipeRight, toHome }) => {
  const [isBlockchainFeeModalOpen, setIsBlockchainFeeModalOpen] =
    useState(false);
  const [isMinimumReceivedModalOpen, setIsMinimumReceivedModalOpen] =
    useState(false);
  const [isExchangeRateModalOpen, setIsExchangeRateModalOpen] = useState(false);
  const [isPriceImpactModalOpen, setIsPriceImpactModalOpen] = useState(false);
  const handlers = useSwipeable({
    onSwipedLeft: () => onSwipeLeft?.(),
    onSwipedRight: () => onSwipeRight?.(),
    trackMouse: true,
    delta: 50,
    swipeDuration: 500,
  });

  const lang = (localStorage.getItem(LANG_KEY) as 'ru' | 'en') || 'ru';
  const t = fourTheoryLocales[lang][5];

  const mainConceptsContent = React.useMemo(() => {
    if (lang === 'en' && typeof t.mainConcepts === 'string') {
      const stonfiTarget = 'STON.fi';
      const ongoingTarget = 'Ongoing farm';

      const replaceWithStrong = (text: string, target: string) => {
        const pieces = [] as React.ReactNode[];
        let startIndex = 0;
        let matchIndex = text.indexOf(target);

        while (matchIndex !== -1) {
          if (matchIndex > startIndex) {
            pieces.push(text.slice(startIndex, matchIndex));
          }
          pieces.push(
            <strong key={`${target}-${matchIndex}`}>{target}</strong>
          );
          startIndex = matchIndex + target.length;
          matchIndex = text.indexOf(target, startIndex);
        }

        if (startIndex < text.length) {
          pieces.push(text.slice(startIndex));
        }

        return pieces;
      };

      let content: React.ReactNode[] | string = t.mainConcepts;
      content = replaceWithStrong(t.mainConcepts, stonfiTarget);
      content = content.flatMap((node) =>
        typeof node === 'string'
          ? replaceWithStrong(node, ongoingTarget)
          : [node]
      );

      return <>{content}</>;
    }

    return t.mainConcepts;
  }, [lang, t.mainConcepts]);

  return (
    <div {...handlers}>
      <GlobalFonts />
      <Container>
        <ScrollContainer>
          <HomeButton onClick={toHome}>
            <HomeButtonIcon src="/assets/homeIcon.svg" />
          </HomeButton>
          <Header>
            <Title>{t.title}</Title>
            <TheoryButton>{t.theory}</TheoryButton>
          </Header>
          <QuestionBlock>
            <QuestionText>{t.question}</QuestionText>
          </QuestionBlock>
          <ButtonContainer>
            <Description>{mainConceptsContent}</Description>
            <StyledButton onClick={() => setIsBlockchainFeeModalOpen(true)}>
              <ButtonText>{t.rewards}</ButtonText>
              <ButtonIcon src="/assets/strelka.svg" />
            </StyledButton>

            <StyledButton onClick={() => setIsMinimumReceivedModalOpen(true)}>
              <ButtonText>{t.farmingPeriod}</ButtonText>
              <ButtonIcon src="/assets/strelka.svg" />
            </StyledButton>

            <StyledButton onClick={() => setIsExchangeRateModalOpen(true)}>
              <ButtonText>{t.lpTokensLock}</ButtonText>
              <ButtonIcon src="/assets/strelka.svg" />
            </StyledButton>

            <StyledButton onClick={() => setIsPriceImpactModalOpen(true)}>
              <ButtonText>{t.farmApr}</ButtonText>
              <ButtonIcon src="/assets/strelka.svg" />
            </StyledButton>
          </ButtonContainer>
          <AnimatePresence>
            {isBlockchainFeeModalOpen && (
              <ModalOverlay
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
                onClick={() => setIsBlockchainFeeModalOpen(false)}
              >
                <ModalContent
                  onClick={(e) => e.stopPropagation()}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={modalContentVariants}
                >
                  <CloseButton
                    onClick={() => setIsBlockchainFeeModalOpen(false)}
                  />
                  <ModalTitle>{t.rewardsTitle}</ModalTitle>
                  <ModalText>{t.rewardsText}</ModalText>
                </ModalContent>
              </ModalOverlay>
            )}

            {isMinimumReceivedModalOpen && (
              <ModalOverlay
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
                onClick={() => setIsMinimumReceivedModalOpen(false)}
              >
                <ModalContent
                  onClick={(e) => e.stopPropagation()}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={modalContentVariants}
                >
                  <CloseButton
                    onClick={() => setIsMinimumReceivedModalOpen(false)}
                  />
                  <ModalTitle>{t.farmingPeriodTitle}</ModalTitle>
                  <ModalText>{t.farmingPeriodText}</ModalText>
                </ModalContent>
              </ModalOverlay>
            )}

            {isExchangeRateModalOpen && (
              <ModalOverlay
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
                onClick={() => setIsExchangeRateModalOpen(false)}
              >
                <ModalContent
                  onClick={(e) => e.stopPropagation()}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={modalContentVariants}
                >
                  <CloseButton
                    onClick={() => setIsExchangeRateModalOpen(false)}
                  />
                  <ModalTitle>{t.lpTokensLockTitle}</ModalTitle>
                  <ModalText>{t.lpTokensLockText}</ModalText>
                </ModalContent>
              </ModalOverlay>
            )}

            {isPriceImpactModalOpen && (
              <ModalOverlay
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={modalVariants}
                onClick={() => setIsPriceImpactModalOpen(false)}
              >
                <ModalContent
                  onClick={(e) => e.stopPropagation()}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={modalContentVariants}
                >
                  <CloseButton
                    onClick={() => setIsPriceImpactModalOpen(false)}
                  />
                  <ModalTitle>{t.farmAprTitle}</ModalTitle>
                  <ModalText>{t.farmAprText}</ModalText>
                </ModalContent>
              </ModalOverlay>
            )}
          </AnimatePresence>
          <Footer
            textLeft={t.back}
            textRight={t.next}
            onSwipeLeft={onSwipeLeft}
            onSwipeRight={onSwipeRight}
          ></Footer>
        </ScrollContainer>
      </Container>
    </div>
  );
};

export default FourTheory5;
